import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaCheckCircle, FaRegCircle, FaStar, FaArrowUp, FaArrowDown, FaMinus } from 'react-icons/fa';
import LinkifiedText from '../components/common/LinkifiedText';

import Spinner from '../components/common/Spinner/Spinner';
import { SearchableSelectBase } from '../components/common/SearchableSelect/SearchableSelect';
import RadarChart from '../components/common/RadarChart/RadarChart';
import { INDICADORES, getFaixaMaturidade, MAPA_RESPOSTA } from '../constants/redeFederal';
import { buildRedeFederalRadarData } from '../utils/radarUtils';
import './RedeFederal.css';

const logo = '/logo_gif.gif';
const pct = (v) => (typeof v === 'number' ? (v * 100).toFixed(1).replace('.', ',') + '%' : '-');
const pp = (v) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1).replace('.', ',')} pp`;

const RedeFederalInstituicao = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [inst, setInst] = useState(null);
  const [questoes, setQuestoes] = useState([]);
  const [mediasRF, setMediasRF] = useState(null);
  const [aggByCodigo, setAggByCodigo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [busca, setBusca] = useState('');
  const [tema, setTema] = useState('Todos');
  const [activeTab, setActiveTab] = useState('visaoGeral');

  useEffect(() => {
    document.body.classList.add('diagnostico-publico');
    return () => document.body.classList.remove('diagnostico-publico');
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [instResp, idxResp, qAggResp] = await Promise.all([
          fetch(`/rede-federal/inst/${slug}.json`),
          fetch('/rede-federal/index.json'),
          fetch('/rede-federal/questoes.json'),
        ]);
        if (!instResp.ok) throw new Error('Instituição não encontrada na base da Rede Federal.');
        const instData = await instResp.json();
        const idxData = idxResp.ok ? await idxResp.json() : null;
        const aggData = qAggResp.ok ? await qAggResp.json() : null;
        
        const qs = aggData ? aggData.questoes.map((q) => ({
          id: q.qKey,
          codigo: q.codigo,
          enunciado: q.enunciado,
          eixo: q.eixo,
          questoes_adicionais: q.subs
        })) : [];

        const aggMap = {};
        if (aggData) aggData.questoes.forEach((q) => { aggMap[q.codigo] = q; });
        if (active) {
          setInst(instData);
          setQuestoes(qs);
          setMediasRF(idxData?.mediasRedeFederal || null);
          setAggByCodigo(aggMap);
        }
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [slug]);

  // Mapa código (Q####) -> metadados da questão
  const questaoByKey = useMemo(() => {
    const map = {};
    questoes.forEach((q) => {
      if (!q.codigo) return;
      const key = String(q.codigo).startsWith('Q') ? String(q.codigo) : `Q${q.codigo}`;
      map[key] = q;
    });
    return map;
  }, [questoes]);

  // Respostas enriquecidas
  const respostasEnriquecidas = useMemo(() => {
    if (!inst) return [];
    return Object.entries(inst.respostas).map(([key, resp]) => {
      const q = questaoByKey[key] || {};
      const subTexts = Array.isArray(q.questoes_adicionais) ? q.questoes_adicionais : [];
      const marcadas = [];
      if (resp.itens) {
        Object.keys(resp.itens).forEach((idx) => {
          if (resp.itens[idx]) {
            const txt = subTexts[parseInt(idx, 10) - 1];
            marcadas.push(txt || `Item ${idx}`);
          }
        });
      }
      const codigo = q.codigo || key.replace(/^Q/, '');
      const agg = aggByCodigo[codigo];
      let referencia = null;
      let posicao = null;
      if (agg && agg.ranking && agg.ranking.length) {
        referencia = agg.ranking[0];
        const idx = agg.ranking.findIndex((x) => x.sigla === inst.sigla);
        if (idx >= 0) posicao = { rank: idx + 1, total: agg.ranking.length };
      }
      return {
        key,
        codigo,
        enunciado: q.enunciado || '(Questão não localizada no catálogo atual)',
        eixo: q.eixo || 'Outros',
        valor: resp.valor,
        respostaPrincipal: MAPA_RESPOSTA[resp.valor] || 'Não informada',
        evidencia: resp.evidencia || '',
        subTotal: subTexts.length,
        marcadas,
        referencia,
        posicao,
      };
    }).sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }));
  }, [inst, questaoByKey, aggByCodigo]);

  const temas = useMemo(() => {
    const set = new Set(respostasEnriquecidas.map((r) => r.eixo));
    return ['Todos', ...Array.from(set).sort()];
  }, [respostasEnriquecidas]);

  const radarComparativo = useMemo(() => (
    buildRedeFederalRadarData({
      indicadores: inst?.indicadores || {},
      benchmark: mediasRF || {},
      sigla: inst?.sigla || 'Instituição',
      benchmarkLabel: 'Média da Rede Federal',
    })
  ), [inst, mediasRF]);

  const respostasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return respostasEnriquecidas.filter((r) => {
      const matchTema = tema === 'Todos' || r.eixo === tema;
      const matchBusca = !termo || r.enunciado.toLowerCase().includes(termo) || r.codigo.toLowerCase().includes(termo) || r.evidencia.toLowerCase().includes(termo);
      return matchTema && matchBusca;
    });
  }, [respostasEnriquecidas, busca, tema]);

  if (loading) return <Spinner text="Carregando dados da instituição..." fullScreen />;
  if (error) {
    return (
      <div className="rf-container">
        <button className="rf-back-btn" onClick={() => navigate('/rede-federal')}><FaArrowLeft /> Voltar à Rede Federal</button>
        <div className="rf-empty"><p>{error}</p></div>
      </div>
    );
  }

  const indicadoresComValor = INDICADORES.filter((i) => typeof inst.indicadores[i.id] === 'number');

  return (
    <div className="rf-container">
      <button className="rf-back-btn no-print" onClick={() => navigate('/rede-federal')}><FaArrowLeft /> Voltar à Rede Federal</button>

      <header className="rf-hero">
        <img src={logo} alt="Mago" className="rf-logo" />
        <div>
          <span className="rf-inst-sigla">{inst.sigla}</span>
          <h1>{inst.nome}</h1>
          <p>Indicadores, respostas originais do TCU, justificativas e evidências públicas — Rede Federal · <strong>iESGo {inst.fonte?.replace('TCU ', '') || '2024'}</strong>.</p>
        </div>
      </header>

      {/* Abas */}
      <nav className="rf-tabs no-print">
        <button
          className={`rf-tab ${activeTab === 'visaoGeral' ? 'active' : ''}`}
          onClick={() => setActiveTab('visaoGeral')}
          type="button"
        >
          Visão Geral
        </button>
        <button
          className={`rf-tab ${activeTab === 'questoes' ? 'active' : ''}`}
          onClick={() => setActiveTab('questoes')}
          type="button"
        >
          Questões iESGo 2024 <span className="rf-tab-count">{inst.totalQuestoes}</span>
        </button>
      </nav>

      {/* Indicadores */}
      <section className="rf-ind-wrap" style={{ display: activeTab === 'visaoGeral' ? '' : 'none' }}>
        <div className="rf-radar-section">
          <RadarChart
            title={`Radar comparativo - ${inst.sigla}`}
            data={radarComparativo}
            ariaLabel={`Radar comparativo entre ${inst.sigla} e a média da Rede Federal`}
            tableCaption={`Valores de ${inst.sigla} comparados com a média da Rede Federal`}
          />
        </div>
        <div className="rf-ind-legend">
          <span><span className="rf-legend-bar inst" /> {inst.sigla}</span>
          <span><span className="rf-legend-bar rede" /> Média da Rede Federal</span>
        </div>
        <div className="rf-ind-grid">
          {indicadoresComValor.map((i) => {
            const v = inst.indicadores[i.id];
            const faixa = getFaixaMaturidade(v * 100);
            const media = mediasRF ? mediasRF[i.id] : null;
            const delta = typeof media === 'number' ? v - media : null;
            const acima = delta !== null && delta > 0.001;
            const abaixo = delta !== null && delta < -0.001;
            return (
              <div key={i.id} className="rf-ind-card" title={i.desc}>
                <div className="rf-ind-top">
                  <span className="rf-ind-sigla">{i.sigla}</span>
                  <span className={`rf-badge ${faixa.classe}`}>{faixa.texto}</span>
                </div>
                <div className={`rf-ind-value ${faixa.classe}`}>{pct(v)}</div>
                <div className="rf-ind-label">{i.label}</div>
                <div className="rf-ind-bar"><span style={{ width: `${Math.min(100, v * 100)}%`, background: faixa.cor }} /></div>
                {typeof media === 'number' && (
                  <>
                    <div className="rf-ind-bar rede"><span style={{ width: `${Math.min(100, media * 100)}%` }} /></div>
                    <div className={`rf-ind-delta ${acima ? 'up' : abaixo ? 'down' : 'flat'}`}>
                      {acima ? <FaArrowUp /> : abaixo ? <FaArrowDown /> : <FaMinus />}
                      {pp(delta)} vs Rede ({pct(media)})
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Respostas TCU */}
      <section className="rf-resp-section" style={{ display: activeTab === 'questoes' ? '' : 'none' }}>
        <div className="rf-resp-header">
          <h2>Respostas originais conforme iESGo 2024 <span className="rf-count">({inst.totalQuestoes} questões)</span></h2>
          <div className="rf-resp-filters">
            <div className="rf-search">
              <FaSearch />
              <input type="text" placeholder="Buscar questão, código ou evidência..." value={busca} onChange={(e) => setBusca(e.target.value)} />
            </div>
            <SearchableSelectBase
              options={temas.map((t) => ({ id: t, label: t }))}
              value={tema}
              onChange={setTema}
              placeholder="Filtrar por tema..."
              sx={{ minWidth: 240 }}
            />
          </div>
        </div>

        <div className="rf-resp-list">
          {respostasFiltradas.map((r) => {
            const faixa = getFaixaMaturidade((r.valor >= 8 ? 85 : r.valor === 7 ? 50 : r.valor === 6 ? 15 : 0));
            return (
              <article key={r.key} className="rf-resp-item">
                <div className="rf-resp-item-head">
                  <span className="rf-resp-codigo">{r.codigo}</span>
                  <span className="rf-resp-tema">{r.eixo}</span>
                  <span className={`rf-badge ${faixa.classe}`}>{r.respostaPrincipal}</span>
                  {r.posicao && (
                    <span className="rf-resp-rank" title="Posição desta instituição entre as que responderam esta questão na Rede Federal">
                      {r.posicao.rank}º / {r.posicao.total} na Rede
                    </span>
                  )}
                </div>
                <p className="rf-resp-enunciado">{r.enunciado}</p>

                {r.referencia && (
                  <div className={`rf-resp-ref ${r.referencia.sigla === inst.sigla ? 'is-self' : ''}`}>
                    <FaStar />
                    {r.referencia.sigla === inst.sigla
                      ? <span><strong>Esta instituição é referência</strong> da Rede Federal nesta questão.</span>
                      : <span>Referência na Rede: <Link to={`/rede-federal/${r.referencia.slug}`}>{r.referencia.sigla}</Link> (valor {r.referencia.valor}, {r.referencia.itens} práticas)</span>}
                  </div>
                )}

                {r.subTotal > 0 && (
                  <div className="rf-resp-itens">
                    <strong>Práticas adotadas ({r.marcadas.length}/{r.subTotal}):</strong>
                    {r.marcadas.length > 0 ? (
                      <ul>
                        {r.marcadas.map((m, idx) => (
                          <li key={idx}><FaCheckCircle className="rf-check" /> {m}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="rf-none"><FaRegCircle /> Nenhuma prática marcada.</p>
                    )}
                  </div>
                )}

                {r.evidencia && (
                  <div className="rf-resp-evidencia">
                    <strong>Justificativa / Evidência:</strong>
                    <p><LinkifiedText text={r.evidencia} /></p>
                  </div>
                )}
              </article>
            );
          })}
          {respostasFiltradas.length === 0 && (
            <div className="rf-empty"><p>Nenhuma questão corresponde ao filtro.</p></div>
          )}
        </div>
      </section>

      <p className="rf-footnote">
        Fonte: dados abertos do TCU (iESGo 2024). Para comparar com outras instituições, volte ao <Link to="/rede-federal">panorama da Rede Federal</Link>.
      </p>
    </div>
  );
};

export default RedeFederalInstituicao;
